import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Goal, useFinanceStore } from '../store/useFinanceStore';
import { toast } from 'react-hot-toast';
import { updateGoalWithTransaction } from '../services/goalService';

interface EditGoalModalProps {
  goal: Goal;
  isOpen: boolean;
  onClose: () => void;
}

const EditGoalModal: React.FC<EditGoalModalProps> = ({ goal, isOpen, onClose }) => {
  const { updateGoal, addTransaction, calculateGoalAnalytics } = useFinanceStore();
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    deadline: '',
    adjustmentValue: '',
    adjustmentMode: 'add' as 'add' | 'remove'
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && goal) {
      setFormData({
        name: goal.name,
        targetAmount: goal.targetAmount.toString(),
        deadline: goal.deadline,
        adjustmentValue: '',
        adjustmentMode: 'add'
      });
    }
  }, [isOpen, goal]);

  const projectedAmount = (() => {
    const adjustment = parseFloat(formData.adjustmentValue || '0');
    if (Number.isNaN(adjustment)) return goal.currentAmount;
    const delta = formData.adjustmentMode === 'add' ? adjustment : -adjustment;
    return Math.max(0, goal.currentAmount + delta);
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.targetAmount || !formData.deadline) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    try {
      const updatedData = {
        name: formData.name,
        targetAmount: parseFloat(formData.targetAmount),
        currentAmount: projectedAmount,
        deadline: formData.deadline
      };

      const result = await updateGoalWithTransaction(
        goal.id,
        goal,
        updatedData,
        updateGoal,
        addTransaction,
        calculateGoalAnalytics
      );

      if (result.success) {
        toast.success(result.message);
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
      toast.error('Erro ao atualizar meta');
    } finally {
      setIsLoading(false);
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-800 to-slate-900 p-6 shadow-2xl"
            style={{
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 10px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-gradient-to-br from-slate-800 to-slate-900 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-full">
                  <Target className="text-blue-400" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Editar Meta</h2>
                  <p className="text-xs text-slate-400">Ajuste o nome, meta e valor guardado sem sair do fluxo.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="p-2 text-gray-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all duration-200 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 pb-2">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Título da Meta
                </label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Viagem para Europa"
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Valor da Meta
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.targetAmount}
                      onChange={(e) => setFormData((prev) => ({ ...prev, targetAmount: e.target.value }))}
                      placeholder="0,00"
                      disabled={isLoading}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Valor guardado atual
                  </label>
                  <div className="rounded-lg border border-slate-700/60 bg-slate-800/80 px-4 py-3 text-white">
                    R$ {goal.currentAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-700/60 bg-slate-800/60 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                  <p className="text-sm font-medium text-white">Editar valor guardado</p>
                  <div className="flex rounded-full border border-slate-600/70 bg-slate-900/50 text-xs font-semibold text-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, adjustmentMode: 'add' }))}
                      className={`px-3 py-1 transition-colors ${
                        formData.adjustmentMode === 'add'
                          ? 'bg-emerald-500/20 text-emerald-200'
                          : 'hover:bg-slate-700/60'
                      }`}
                    >
                      Adicionar
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, adjustmentMode: 'remove' }))}
                      className={`px-3 py-1 transition-colors ${
                        formData.adjustmentMode === 'remove'
                          ? 'bg-rose-500/20 text-rose-200'
                          : 'hover:bg-slate-700/60'
                      }`}
                    >
                      Retirar
                    </button>
                  </div>
                </div>

                <p className="text-xs text-slate-400">
                  Adicione novas contribuições ou retire valores para atualizar a meta.
                </p>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.adjustmentValue}
                    onChange={(e) => setFormData((prev) => ({ ...prev, adjustmentValue: e.target.value }))}
                    placeholder="0,00"
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/40 border border-slate-700/60 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-slate-400">
                  Novo valor ficará em <span className="font-semibold text-white">R$ {projectedAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span> após {formData.adjustmentMode === 'add' ? 'adicionar' : 'retirar'} este valor.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Data Limite
                </label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData((prev) => ({ ...prev, deadline: e.target.value }))}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-slate-700/50 hover:bg-slate-600/50 text-gray-300 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default EditGoalModal;
